<template>
  <div class="chatbot-interface flex flex-col h-full bg-white rounded-lg overflow-hidden relative">
    <!-- Header Section -->
    <ChatbotHeader
      :businessRole="userData?.business_role"
      :environment="environment"
      :userName="userData?.name"
      :userAvatar="userAvatarForHeader"
      @toggle-history="toggleChatHistory"
    />
    
    <!-- FastMode Information Banner -->
    <div
      v-if="isFastmodeActive && !isFastModeBannerDismissed"
      class="bg-yellow-50 border-l-4 border-yellow-400 p-4 relative"
      :class="{ 'filter blur-sm': isChatHistoryOpen }"
    >
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <!-- Information Icon -->
          <svg class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <p class="text-sm text-yellow-700">
            Financial data is only accessible in DeepMode. Please switch modes if you need financial information.
          </p>
        </div>
        <!-- Close Button -->
        <button
          @click="dismissFastModeBanner"
          class="ml-auto flex-shrink-0 text-yellow-400 hover:text-yellow-500 focus:outline-none"
        >
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
    
    <!-- Messages Section -->
    <div class="flex-1 relative overflow-hidden" :class="{ 'filter blur-sm': isChatHistoryOpen }">
      <ChatbotMessages
        :messages="messages"
        :userData="userData"
        class="absolute inset-0"
      />
    </div>
    
    <!-- Initial Loading Indicator -->
    <div v-if="isInitialLoading" class="flex justify-center items-center py-3 bg-blue-50 text-blue-600 text-sm" :class="{ 'filter blur-sm': isChatHistoryOpen }">
      <div class="flex items-center">
        <span class="animate-pulse">Initialisierung</span>
      </div>
    </div>
    
    <!-- Response Loading Indicator -->
    <div v-if="isResponseLoading && !isInitialLoading" class="flex justify-center items-center py-3 bg-blue-50 text-blue-600 text-sm" :class="{ 'filter blur-sm': isChatHistoryOpen }">
      <div class="flex items-center">
        <span class="animate-pulse">{{ currentLoadingMessage }}</span>
      </div>
    </div>
    
    <!-- Error Message -->
    <div v-if="error" class="bg-red-50 text-red-600 p-2 text-sm text-center" :class="{ 'filter blur-sm': isChatHistoryOpen }">
      {{ error }}
    </div>
    
    <!-- Input Section -->
    <div :class="{ 'filter blur-sm': isChatHistoryOpen }">
      <ChatbotInput
        @send-message="handleSendMessage"
        @mode-changed="handleModeChanged"
        :disabled="isInitialLoading || isResponseLoading || isChatHistoryOpen"
      />
    </div>
    
    <!-- Overlay to close chat history when clicking outside -->
    <div
      v-if="isChatHistoryOpen"
      class="absolute inset-0 bg-black bg-opacity-30 z-40"
      @click="closeChatHistory"
    ></div>
    
    <!-- Chat History List -->
    <ChatHistoryList
      :isOpen="isChatHistoryOpen"
      :userChats="userChats"
      :currentChatId="currentChat?.id || ''"
      @close="closeChatHistory"
      @select-chat="handleSelectChat"
      @delete-chat="handleDeleteChat"
      @create-new-chat="handleCreateNewChat"
      class="z-50"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, onMounted, ref, onUnmounted, watch } from 'vue';
import { BusinessRole } from '@/types/businessRole';
import { ChatbotEnv } from '@/types/chatbotEnv';
import { ChatMessage, ChatWrapper, ChatWrapperWithMessages } from '@/types/chat';
import { chatService } from '@/services/chatService';
import { messageManagerService } from '@/services/llm';
import ChatbotHeader from './ChatbotHeader.vue';
import ChatbotMessages from './ChatbotMessages.vue';
import ChatbotInput from './ChatbotInput.vue';
import ChatHistoryList from './ChatHistoryList.vue';

interface UserData {
  name?: string;
  avatar?: string | null;
  business_role?: string;
  id?: string;
}

export default defineComponent({
  name: 'ChatbotInterface',
  components: {
    ChatbotHeader,
    ChatbotMessages,
    ChatbotInput,
    ChatHistoryList
  },
  props: {
    userData: {
      type: Object as PropType<UserData>,
      default: () => ({
        name: 'User',
        avatar: null,
        business_role: BusinessRole.PLANNER
      })
    },
    environment: {
      type: String as PropType<ChatbotEnv>,
      default: ChatbotEnv.GLOBAL
    },
    userId: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    // State variables (moved from useChat)
    const currentChat = ref<ChatWrapperWithMessages | null>(null);
    const userChats = ref<ChatWrapper[]>([]);
    const isInitialLoading = ref(false);
    const isResponseLoading = ref(false);
    const error = ref<string | null>(null);
    const isChatHistoryOpen = ref(false);
    const isFastmodeActive = ref(true); // Default to FastMode
    const isFastModeBannerDismissed = ref(false); // Track if the FastMode banner has been dismissed
    
    // Base loading indicator messages
    const baseLoadingMessages = [
      "Checking Fraport knowledgebase...",
      "Checking current active user informations...",
      "Checking other current time and date informations...",
      "Crafting a response...",
      "Formatting response...",
      "There might be an issue with the connection, please reload..."
    ];
    
    // PowerBI message that should only appear in DeepMode
    const powerBIMessage = "Checking PowerBI reports if needed...";
    
    // Computed property for loading messages based on mode
    const loadingMessages = computed(() => {
      if (isFastmodeActive.value) {
        // In FastMode, exclude the PowerBI message
        return baseLoadingMessages;
      } else {
        // In DeepMode, include the PowerBI message after the time/date info
        return [
          ...baseLoadingMessages.slice(0, 3),
          powerBIMessage,
          ...baseLoadingMessages.slice(3)
        ];
      }
    });
    
    const currentLoadingMessage = ref(baseLoadingMessages[0]);
    let loadingMessageInterval: number | null = null;
    
    // Track the polling interval and last message timestamp
    let pollingInterval: number | null = null;
    const lastMessageTimestamp = ref<Date | null>(null);
    const isPolling = ref(false); // Flag to prevent concurrent polling
    
    // Computed properties
    const messages = computed(() => {
      if (!currentChat.value) return [];
      return currentChat.value.messages.map(msg => ({...msg}));
    });
    
    // Convert null avatar to undefined to satisfy TypeScript
    const userAvatarForHeader = computed(() => {
      return props.userData?.avatar === null ? undefined : props.userData?.avatar;
    });
    
    // Clean up intervals when component is unmounted
    onUnmounted(() => {
      console.log('ChatbotInterface - Component unmounted, cleaning up intervals');
      stopPolling();
      stopLoadingMessageRotation();
    });
    
    /**
     * Start rotating response loading messages at random intervals
     */
    const startLoadingMessageRotation = () => {
      // Stop any existing rotation
      stopLoadingMessageRotation();
      
      // Set initial message
      currentLoadingMessage.value = loadingMessages.value[0];
      
      // Function to rotate to next message
      const rotateMessage = () => {
        // Get current messages array
        const currentMessages = loadingMessages.value;
        
        // Get current index
        const currentIndex = currentMessages.indexOf(currentLoadingMessage.value);
        
        // If current message isn't in the array (could happen when switching modes),
        // reset to the first message
        if (currentIndex === -1) {
          currentLoadingMessage.value = currentMessages[0];
          loadingMessageInterval = window.setTimeout(rotateMessage, 1000);
          return;
        }
        
        // Calculate next index (loop back to start if at end)
        const nextIndex = Math.min((currentIndex + 1), currentMessages.length - 1);
        
        // Set next message
        currentLoadingMessage.value = currentMessages[nextIndex];
        
        // Schedule next rotation with random timing (0.5 to 5 seconds)
        const randomDelay = Math.floor(Math.random() * 4500) + 1500; // 1500-6000ms
        if(nextIndex === currentMessages.length - 2) {
            loadingMessageInterval = window.setTimeout(rotateMessage, 30000);
        } else if(nextIndex === currentMessages.length - 1) {
            if (loadingMessageInterval !== null) {
                window.clearTimeout(loadingMessageInterval);
                loadingMessageInterval = null;
            }
        } else {
          loadingMessageInterval = window.setTimeout(rotateMessage, randomDelay);
        }
      };
      
      // Start the rotation
      loadingMessageInterval = window.setTimeout(rotateMessage, 1000);
    };
    
    /**
     * Stop rotating response loading messages
     */
    const stopLoadingMessageRotation = () => {
      if (loadingMessageInterval !== null) {
        window.clearTimeout(loadingMessageInterval);
        loadingMessageInterval = null;
      }
    };
    
    /**
     * Start polling for new messages in the current chat
     * This will be called when loading a chat or creating a new chat
     */
    const startPolling = () => {
      if (!currentChat.value) {
        console.error('Cannot start polling: No active chat');
        return;
      }
      
      // Stop any existing polling
      stopPolling();
      
      console.log('ChatbotInterface - Starting polling for chat:', currentChat.value.id);
      
      // Initialize the last message timestamp from the current chat's messages
      if (currentChat.value.messages.length > 0) {
        const messages = currentChat.value.messages;
        const lastMessage = messages[messages.length - 1];
        lastMessageTimestamp.value = lastMessage.created_at;
        console.log('ChatbotInterface - Initialized last message timestamp:', lastMessageTimestamp.value);
      } else {
        lastMessageTimestamp.value = null;
        console.log('ChatbotInterface - No messages yet, timestamp reset to null');
      }
      
      // Start polling every 3 seconds
      pollingInterval = window.setInterval(() => {
        fetchNewMessages();
      }, 3000);
    };
    
    /**
     * Stop polling for new messages
     */
    const stopPolling = () => {
      if (pollingInterval !== null) {
        console.log('ChatbotInterface - Stopping polling');
        window.clearInterval(pollingInterval);
        pollingInterval = null;
      }
      
      // Reset polling state
      isPolling.value = false;
    };
    
    /**
     * Fetch new messages for the current chat
     * Only fetches messages newer than the last known message
     */
    const fetchNewMessages = async () => {
      if (!currentChat.value) {
        console.warn('ChatbotInterface - No active chat, cannot fetch messages');
        return;
      }
      
      // Prevent concurrent polling requests
      if (isPolling.value) {
        console.log('ChatbotInterface - Already polling, skipping this cycle');
        return;
      }
      
      isPolling.value = true;
      
      try {
        console.log('ChatbotInterface - Fetching new messages after:', lastMessageTimestamp.value);
        
        // Only fetch the chat wrapper once if needed (first poll)
        if (currentChat.value.messages.length === 0) {
          // For empty chats, fetch everything
          const chatWithMessages = await chatService.getChatWithMessages(currentChat.value.id);
          
          if (!chatWithMessages) {
            console.warn('ChatbotInterface - Failed to fetch messages');
            isPolling.value = false;
            return;
          }
          
          // Update the current chat with all messages
          currentChat.value.messages = chatWithMessages.messages;
          
          // Update the last message timestamp if we have messages
          if (chatWithMessages.messages.length > 0) {
            const lastMessage = chatWithMessages.messages[chatWithMessages.messages.length - 1];
            lastMessageTimestamp.value = lastMessage.created_at;
            console.log('ChatbotInterface - Updated last message timestamp:', lastMessageTimestamp.value);
          }
        } else {
          // For existing chats with messages, only fetch new messages
          const newMessages = await chatService.getNewChatMessages(
            currentChat.value.id,
            lastMessageTimestamp.value || undefined
          );
          
          console.log(`ChatbotInterface - Fetched ${newMessages.length} new messages`);
          
          // Process any new messages
          if (newMessages.length > 0) {
            for (const newMessage of newMessages) {
              // Check if this is a message we already have (avoid duplicates)
              const existingMessageIndex = currentChat.value.messages.findIndex(
                msg => msg.id === newMessage.id
              );
              
              if (!(existingMessageIndex >= 0)) {
                  console.log('ChatbotInterface - Adding new message to UI:', newMessage);
                  // Add the new message
                  currentChat.value.messages = [...currentChat.value.messages, newMessage];

                  // Update last_message_date
                  currentChat.value.last_message_date = newMessage.created_at;

                  // Update in userChats list
                  updateChatInList(currentChat.value);

                  // If this is an AI message, we can enable the input again
                  if (newMessage.is_ai_message) {
                      isResponseLoading.value = false;
                      stopLoadingMessageRotation();
                  }

                  // Update the last message timestamp
                  lastMessageTimestamp.value = newMessage.created_at;
                  console.log('ChatbotInterface - Updated last message timestamp:', lastMessageTimestamp.value);
              }
            }
          } else {
              isPolling.value = false;
          }
        }
      } catch (err) {
        console.error('Error fetching new messages:', err);
      } finally {
        isPolling.value = false;
      }
    };
    
    /**
     * Create a new chat
     * @param title Optional title for the chat
     */
    const createNewChat = async (title?: string): Promise<ChatWrapper | null> => {
      // Stop any existing polling
      stopPolling();
      
      if (!props.userId) {
        error.value = 'User ID is required to create a chat';
        return null;
      }
      
      isResponseLoading.value = true;
      error.value = null;
      
      try {
        const chat = await chatService.createChat(props.userId, props.environment, title);
        
        if (chat) {
          // Add to user chats
          userChats.value = [chat, ...userChats.value];
          
          // Set as current chat with empty messages array
          currentChat.value = {
            ...chat,
            messages: []
          };
          
          // Start polling for messages for the newly created chat
          startPolling();
        }
        
        return chat;
      } catch (err) {
        error.value = 'Failed to create chat';
        console.error('Error creating chat:', err);
        return null;
      } finally {
        isResponseLoading.value = false;
      }
    };
    
    
    /**
     * Load a chat by ID and set it as the current chat
     * @param chatId The ID of the chat to load
     */
    const loadChat = async (chatId: string): Promise<ChatWrapperWithMessages | null> => {
      // Stop any existing polling
      stopPolling();
      
      isResponseLoading.value = true;
      error.value = null;
      
      try {
        const chatWithMessages = await chatService.getChatWithMessages(chatId);
        
        if (chatWithMessages) {
          currentChat.value = chatWithMessages;
          
          // Start polling for messages for the newly loaded chat
          startPolling();
        } else {
          error.value = 'Chat not found';
        }
        
        return chatWithMessages;
      } catch (err) {
        error.value = 'Failed to load chat';
        console.error('Error loading chat:', err);
        return null;
      } finally {
        isResponseLoading.value = false;
      }
    };
    
    /**
     * Initialize chat for the given environment
     * - Loads all chats for the user and environment
     * - Loads the latest chat's messages
     * - If the latest chat has messages, creates a new chat
     * - If no chats exist or the latest chat has no messages, uses it as the current chat
     */
    const initializeChat = async (): Promise<void> => {
      // Check if user ID is provided
      if (!props.userId) {
        error.value = 'User ID is required to initialize chat';
        return;
      }
      
      isInitialLoading.value = true;
      error.value = null;
      
      try {
        // Load all chats for the user and environment
        const chats = await chatService.getUserChats(props.userId, props.environment);
        userChats.value = chats;
        
        if (chats.length === 0) {
          // No existing chats, create a new one
          const defaultTitle = `Chat with ${props.userData?.name || 'User'}`;
          await createNewChat(defaultTitle);
          return;
        }
        
        // Get the latest chat (should be the first one since they're sorted by last_message_date)
        const latestChat = chats[0];
        
        // Load the latest chat's messages
        const chatWithMessages = await loadChat(latestChat.id);
        
        if (!chatWithMessages) {
          // Failed to load chat, create a new one
          const defaultTitle = `Chat with ${props.userData?.name || 'User'}`;
          await createNewChat(defaultTitle);
          return;
        }
        
        if (chatWithMessages.messages.length === 0) {
          // Latest chat has no messages, use it as the current chat
          // (loadChat already sets it as the current chat and subscribes to messages)
          return;
        }
        
        // Latest chat has messages, create a new chat
        const defaultTitle = `Chat with ${props.userData?.name || 'User'}`;
        await createNewChat(defaultTitle);
      } catch (err) {
        error.value = 'Failed to initialize chat';
        console.error('Error initializing chat:', err);
      } finally {
        isInitialLoading.value = false;
      }
    };
    
    /**
     * Send a user message in the current chat
     * @param content The message content
     */
    const sendUserMessage = async (content: string): Promise<ChatMessage | null> => {
      if (!currentChat.value) {
        error.value = 'No active chat';
        return null;
      }
      
      if (!props.userId) {
        error.value = 'User ID is required to send messages';
        return null;
      }
      
      isResponseLoading.value = true;
      error.value = null;
      startLoadingMessageRotation();
      
      try {
        const message = await chatService.addMessage(
          currentChat.value.id,
          content,
          false, // Not an AI message
          props.userId
        );
        
        if (message && currentChat.value) {
          // Add message to current chat
          currentChat.value.messages = [...currentChat.value.messages, message];
          // Update last_message_date
          currentChat.value.last_message_date = message.created_at;
          // Update in userChats list
          updateChatInList(currentChat.value);
        }
        
        return message;
      } catch (err) {
        error.value = 'Failed to send message';
        console.error('Error sending message:', err);
        return null;
      } finally {
        // Note: We don't set isLoading to false here because we're waiting for the AI response
      }
    };
    
    /**
     * Process a user message through the LLM
     * This method only sends the request to the LLM service
     * The AI response will be saved by the Fission function and received via Supabase subscription
     * 
     * @param content The user message content
     */
    const processMessageWithLLM = async (content: string): Promise<void> => {
      if (!currentChat.value) {
        error.value = 'No active chat';
        return;
      }
      
      console.log('ChatbotInterface - Starting to process message');
      console.log('ChatbotInterface - FastMode active:', isFastmodeActive.value);
      
      // First, add the user message to Supabase
      const userMessage = await sendUserMessage(content);
      if (!userMessage) {
        return;
      }
      
      console.log('ChatbotInterface - User message saved to Supabase:', userMessage.id);
      
      isResponseLoading.value = true;
      error.value = null;
      startLoadingMessageRotation();
      
      try {
        // Get the current chat environment
        const environment = currentChat.value.environment;
        
        // Get the chat wrapper ID
        const wrapperId = currentChat.value.id;
        
        console.log('ChatbotInterface - Sending request to Fission function with wrapperId:', wrapperId);
        
        // Process the message through the LLM service
        // This will trigger the Fission function which will save the AI response to Supabase
        // The response will be received via the polling mechanism that checks for new messages
        await messageManagerService.sendLLMRequest({
          userMessage: content,
          userId: props.userId,
          chatWrapperId: wrapperId,
          environment,
          businessRole: props.userData?.business_role,
          isFastmodeActive: isFastmodeActive.value
        });
        
        console.log('ChatbotInterface - Request sent to Fission function, NOT saving AI message in frontend');
        // No need to add AI response here - it will be fetched by the polling mechanism
      } catch (err) {
        error.value = 'Failed to process message with LLM';
        console.error('Error processing message with LLM:', err);
        isResponseLoading.value = false;
        stopLoadingMessageRotation();
      }
    };
    
    /**
     * Delete a chat
     * @param chatId The ID of the chat to delete
     */
    const deleteChat = async (chatId: string): Promise<boolean> => {
      isResponseLoading.value = true;
      error.value = null;
      
      try {
        const success = await chatService.deleteChat(chatId);
        
        if (success) {
          // Remove from userChats list
          userChats.value = userChats.value.filter(chat => chat.id !== chatId);
          
          // Clear current chat if it's the one being deleted
          if (currentChat.value && currentChat.value.id === chatId) {
            clearCurrentChat();
          }
        }
        
        return success;
      } catch (err) {
        error.value = 'Failed to delete chat';
        console.error('Error deleting chat:', err);
        return false;
      } finally {
        isResponseLoading.value = false;
      }
    };
    
    /**
     * Clear the current chat (without deleting it)
     */
    const clearCurrentChat = () => {
      // Stop polling for the current chat
      stopPolling();
      
      // Reset state
      currentChat.value = null;
      lastMessageTimestamp.value = null;
    };
    
    /**
     * Helper function to update a chat in the userChats list
     */
    const updateChatInList = (chat: ChatWrapper) => {
      const index = userChats.value.findIndex(c => c.id === chat.id);
      if (index !== -1) {
        userChats.value = [
          ...userChats.value.slice(0, index),
          chat,
          ...userChats.value.slice(index + 1)
        ];
      }
    };
    
    // Watch for changes to the current chat
    watch(() => currentChat.value?.id, (newChatId, oldChatId) => {
      if (newChatId !== oldChatId) {
        console.log('ChatbotInterface - Chat changed, restarting polling');
        if (newChatId) {
          startPolling();
        } else {
          stopPolling();
        }
      }
    });
    
    // Watch for chat history panel state
    watch(() => isChatHistoryOpen.value, (isOpen) => {
      // When chat history is open, we can pause polling to save resources
      if (isOpen) {
        stopPolling();
      } else if (currentChat.value) {
        // Resume polling when chat history is closed and we have an active chat
        startPolling();
      }
    });
    
    // Initialize chat when the component is mounted
    onMounted(async () => {
      console.log('ChatbotInterface - Component mounted, initializing chat');
      isInitialLoading.value = true;
      
      await initializeChat();
      
      // Immediately fetch messages after initialization
      if (currentChat.value) {
        fetchNewMessages();
      }
      
      // Ensure initial loading is turned off after initialization
      isInitialLoading.value = false;
    });
    
    // Function to handle sending a new message
    const handleSendMessage = async (content: string, fastMode: boolean) => {
      if (!content.trim()) return;
      
      // Update the mode if it was passed
      if (fastMode !== undefined) {
        isFastmodeActive.value = fastMode;
      }
      
      // Process the message with the LLM service
      await processMessageWithLLM(content);
    };
    
    // Function to handle mode changes
    const handleModeChanged = (fastMode: boolean) => {
      console.log('ChatbotInterface - Mode changed to:', fastMode ? 'FastMode' : 'DeepMode');
      isFastmodeActive.value = fastMode;
      
      // Reset banner dismissed state when switching to FastMode
      if (fastMode) {
        isFastModeBannerDismissed.value = false;
      }
      
      // If we're currently showing a loading message, we need to reset it
      // to ensure it's from the correct set of messages for the current mode
      if (isResponseLoading.value) {
        // Restart the loading message rotation with the new set of messages
        stopLoadingMessageRotation();
        startLoadingMessageRotation();
      }
    };
    
    // Function to dismiss the FastMode banner
    const dismissFastModeBanner = () => {
      isFastModeBannerDismissed.value = true;
    };
    
    // Toggle chat history panel
    const toggleChatHistory = () => {
      isChatHistoryOpen.value = !isChatHistoryOpen.value;
    };
    
    // Close chat history panel
    const closeChatHistory = () => {
      isChatHistoryOpen.value = false;
    };
    
    // Handle selecting a chat from the history
    const handleSelectChat = async (chatId: string) => {
      await loadChat(chatId);
      closeChatHistory();
    };
    
    // Handle deleting a chat
    const handleDeleteChat = async (chatId: string) => {
      const success = await deleteChat(chatId);
      
      if (success) {
        // If we deleted the current chat or the last chat, create a new one
        if ((currentChat.value && currentChat.value.id === chatId) || userChats.value.length === 0) {
          await createNewChat(`Chat with ${props.userData?.name || 'User'}`);
        }
        // If we deleted a different chat and there are other chats, load the first one
        else if (currentChat.value === null && userChats.value.length > 0) {
          await loadChat(userChats.value[0].id);
        }
      }
    };
    
    // Handle creating a new chat
    const handleCreateNewChat = async () => {
      await createNewChat(`Chat with ${props.userData?.name || 'User'}`);
      closeChatHistory();
    };
    
    return {
      messages,
      currentChat,
      userChats,
      handleSendMessage,
      handleModeChanged,
      isInitialLoading,
      isResponseLoading,
      error,
      isChatHistoryOpen,
      toggleChatHistory,
      closeChatHistory,
      handleSelectChat,
      handleDeleteChat,
      handleCreateNewChat,
      userAvatarForHeader,
      currentLoadingMessage,
      isFastmodeActive,
      isFastModeBannerDismissed,
      dismissFastModeBanner
    };
  }
});
</script>

<style scoped>
.chatbot-interface {
  height: 100%;
  border-radius: 0.75rem;
  background-color: white;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  min-height: 400px; /* Minimum height to ensure proper display */
}

/* Add a subtle border to enhance the visual separation */
.chatbot-interface {
  border: 1px solid rgba(0, 0, 145, 0.1);
}

/* Ensure smooth transitions between components */
.chatbot-interface > * {
  transition: all 0.3s ease;
}

/* Animation delays for loading indicator */
.animation-delay-200 {
  animation-delay: 0.2s;
}

.animation-delay-400 {
  animation-delay: 0.4s;
}
</style>